/**
 * Spark — the pulsing gold orb at the center of the game.
 *
 * Behavior:
 *   - Always pulses gently (4s breath cycle by default)
 *   - On tap:
 *     · emits a ripple ring AT THE TAP POINT (palpable feedback under finger)
 *     · spawns a floating phrase at a RANDOM POINT AROUND THE SPARK
 *       (not under the finger — the finger would cover it on mobile)
 *   - Honors prefers-reduced-motion (no pulse, no ripples)
 *
 * The phrase origin is normalized (0..1) so the FloatingPhrasesLayer can
 * place the floating fragment at the right spot regardless of viewport size.
 */
import { AnimatePresence, motion } from 'framer-motion';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useGameStore } from '../stores/game.js';
import { useAnchor } from '../onboarding/useAnchor.js';
import { useRandomEventsStore } from '../random-events/store.js';
import { useRandomEvent } from '../random-events/useRandomEvent.js';
import { playSound } from '../sounds/player.js';
import { emit } from '../telemetry/emit.js';
import { CooldownRing } from './CooldownRing.js';
import { SparkArtifacts } from '../visual-artifacts/SparkArtifacts.js';
import styles from './Spark.module.css';

/**
 * Проводники — Spirit guides as small companion orbs around the Spark.
 * Каждый — отдельная точка-присутствие, цвет moss-green (отличается
 * от gold Искры/орбит).
 *
 * b001 Хранитель тишины — справа. b002 Хранитель ур.2 — тот же справа,
 *   но крупнее + ярче (ставит CSS-var --silence-keeper-tier).
 * b003 Алхимик — слева, чуть тёплее зелёный (visibleChange description:
 *   «появляется слева от Искры»).
 *
 * JB 2026-05-26: «b002/b003 рендер 2 и 3 Проводника» — раньше только b001
 * был визуально. Теперь все три Проводника видны как разные точки.
 */
function GuidesLayer(): JSX.Element | null {
  const silenceKeeper = useGameStore(
    (s) => s.state.purchasedUpgrades.get('b001_silence_keeper') ?? 0,
  );
  const silenceKeeperTier2 = useGameStore(
    (s) => s.state.purchasedUpgrades.get('b002_silence_keeper_2') ?? 0,
  );
  const alchemist = useGameStore((s) => s.state.purchasedUpgrades.get('b003_alchemist') ?? 0);
  const gardener = useGameStore((s) => s.state.purchasedUpgrades.get('b020_gardener') ?? 0);

  if (silenceKeeper === 0 && alchemist === 0 && gardener === 0) return null;

  const silenceKeeperClass =
    silenceKeeperTier2 > 0
      ? `${styles.silenceKeeper ?? ''} ${styles.silenceKeeperTier2 ?? ''}`
      : (styles.silenceKeeper ?? '');

  return (
    <>
      {silenceKeeper > 0 && <div className={silenceKeeperClass} aria-hidden />}
      {alchemist > 0 && <div className={styles.alchemist} aria-hidden />}
      {gardener > 0 && <div className={styles.gardener} aria-hidden />}
    </>
  );
}

/** Bucket the total tap count so the marker stream stays low-cardinality. */
function tapBucket(total: number): number {
  if (total < 10) return total;
  if (total < 100) return Math.floor(total / 10) * 10;
  if (total < 1000) return Math.floor(total / 100) * 100;
  return Math.floor(total / 1000) * 1000;
}

interface Ripple {
  readonly key: number;
  readonly x: number;
  readonly y: number;
}

let _rippleKey = 1;

// Phrases appear in this radius (px) around the Spark center.
// Lower bound keeps them off the orb itself; upper bound keeps them in view.
const PHRASE_MIN_RADIUS_PX = 90;
const PHRASE_MAX_RADIUS_PX = 170;

// Strong upward bias — phrases rise, so they need room above the Spark.
// Also keeps them OUT of the bottom UI zone (counter, crystallize button,
// whisper ticker) where they would be illegible against other text.
const PHRASE_VERTICAL_BIAS = -0.7; // -1 = strictly above, 0 = uniform, +1 = strictly below

// Lowest fraction of viewport height a phrase may appear at.
// Bottom 40% is reserved for the counter, button, and whisper ticker.
const PHRASE_MAX_Y_FRACTION = 0.6;

export function Spark(): JSX.Element {
  const tap = useGameStore((s) => s.tap);
  const handleRandomEventTap = useRandomEventsStore((s) => s.handleTap);
  const activeEvent = useRandomEvent();
  const [ripples, setRipples] = useState<readonly Ripple[]>([]);
  // Двойная роль ref'а: (1) измерять rect для расчёта позиции tap-ripple
  // и floating phrases, (2) регистрировать Spark как onboarding anchor
  // чтобы tip-bubble и focus ring могли указать на реальный DOM-узел.
  const buttonRef = useAnchor<HTMLButtonElement>('spark');

  // 2026-05-26 perf: cache rect — раньше `getBoundingClientRect()` вызывался
  // на каждом тапе (3-5 раз/сек) и форсил layout reflow. Теперь измеряем
  // только на mount / resize / scroll через ResizeObserver + window listeners.
  // На стабильном layout (Spark не перемещается) cached rect 100% точен.
  const cachedRectRef = useRef<DOMRect | null>(null);
  useEffect(() => {
    const el = buttonRef.current;
    if (el === null) return;
    const update = (): void => {
      cachedRectRef.current = el.getBoundingClientRect();
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [buttonRef]);

  // Drive the random sensory scheduler. The store reads game state directly
  // so the Spark component does not need to thread totalTaps/epoch through.
  // Note: handleTap deps don't include `buttonRef` — only cachedRectRef
  // is used inside (and refs don't need to be in deps).
  const handleTap = useCallback(
    (clientX: number, clientY: number): void => {
      const rect = cachedRectRef.current;

      // Phrase origin: random point in a ring around the Spark center.
      // We pick polar coords (angle + radius) then convert.
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const sparkCenterX = rect ? rect.left + rect.width / 2 : vw / 2;
      const sparkCenterY = rect ? rect.top + rect.height / 2 : vh / 2;

      const angle = Math.random() * Math.PI * 2; // 0..2π
      const radius =
        PHRASE_MIN_RADIUS_PX + Math.random() * (PHRASE_MAX_RADIUS_PX - PHRASE_MIN_RADIUS_PX);
      // Apply vertical bias by lifting sin component slightly
      const dx = Math.cos(angle) * radius;
      const dy = Math.sin(angle) * radius + PHRASE_VERTICAL_BIAS * radius;

      // Clamp inside viewport with a margin so long phrases stay readable.
      // Vertical max is restricted to the upper area (60% of viewport) so
      // phrases never collide with the bottom UI (counter / button / whisper).
      const margin = 24;
      const phraseX = Math.min(Math.max(sparkCenterX + dx, margin), vw - margin);
      const maxY = vh * PHRASE_MAX_Y_FRACTION;
      const phraseY = Math.min(Math.max(sparkCenterY + dy, margin), maxY);

      const accepted = tap(phraseX / vw, phraseY / vh);

      // JB 2026-05-25: ультракороткий tap-click на КАЖДЫЙ тап (даже
      // rejected) — даёт haptic-like feedback что палец зарегистрирован.
      // Очень тихий, не путается с основным tap_ding (тот играет только
      // на accepted и громче).
      playSound('tap_click');

      // Cooldown-rejected taps: no ripple, no full ding, no telemetry —
      // but tap_click уже сыграл выше как подтверждение касания.
      if (!accepted) return;

      // After the accepted tap, ask the random-events scheduler
      // whether to emit a sensory effect.
      const gs = useGameStore.getState();
      handleRandomEventTap(gs.state.epoch, gs.state.stats.totalTaps);

      // Audio: ding on every accepted tap. Streak variant when the
      // player is locked into rhythm 5+ taps in a row.
      const streak = gs.state.stats.tapsInRhythmCurrent;
      playSound(streak >= 5 ? 'tap_ding_streak' : 'tap_ding');

      // Telemetry: sample every 10th tap to keep the stream tractable.
      if (gs.state.stats.totalTaps % 10 === 0) {
        emit('tap.fired', {
          epoch: gs.state.epoch,
          tap_total_bucket: tapBucket(gs.state.stats.totalTaps),
          layer: gs.liveFloatingPhrases.at(-1)?.phrase.layer ?? 'unknown',
        });
      }

      // Ripple still anchored at the actual tap point — that is the haptic moment.
      if (rect) {
        const localX = clientX - rect.left;
        const localY = clientY - rect.top;
        setRipples((prev) => [...prev, { key: _rippleKey++, x: localX, y: localY }]);
      }
    },
    // 2026-05-26 perf: buttonRef убран из deps (rect теперь читается из
    // cachedRectRef.current — refs не нуждаются в deps). callback стабилен
    // и не пересоздаётся при изменении ref.
    [tap, handleRandomEventTap],
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>): void => {
      // Prevent the synthesized click from firing afterwards
      e.preventDefault();
      handleTap(e.clientX, e.clientY);
    },
    [handleTap],
  );

  const removeRipple = useCallback((key: number): void => {
    setRipples((prev) => prev.filter((r) => r.key !== key));
  }, []);

  // Random sensory effects woven into the Spark's appearance.
  // We only react to events whose id starts with `spark_` — others
  // (e.g. bg_star_twinkle) belong to the StarNetwork.
  const isWarmShift = activeEvent?.id === 'spark_color_warm';
  const isDeeperBreath = activeEvent?.id === 'spark_breath_deeper';

  // Pulse scale envelope changes when "breath_deeper" is active.
  const breathScale = isDeeperBreath ? [1, 1.09, 1] : [1, 1.05, 1];
  const breathOpacity = isDeeperBreath ? [0.85, 1, 0.85] : [0.85, 1, 0.85];

  // CSS class layered on the wrapping <button> so the warm-shift
  // tint travels through the glow/core layers via :where() selectors.
  // The `?? ''` fallbacks satisfy noUncheckedIndexedAccess — CSS-module
  // exports are typed as `string | undefined` but always defined at runtime.
  const sparkClass = styles.spark ?? '';
  const warmClass = styles.warmShift ?? '';
  const buttonClass = isWarmShift ? `${sparkClass} ${warmClass}` : sparkClass;

  return (
    <button
      ref={buttonRef}
      type="button"
      className={buttonClass}
      aria-label="Тап по Искре"
      onPointerDown={onPointerDown}
    >
      <CooldownRing />
      <SparkArtifacts />
      {/* Body-aura — тёплая земля под Искрой когда куплен u011 word_root.
          CSS управляется через --body-aura. */}
      <div className={styles.bodyAura} aria-hidden />
      {/* Постоянный фрактал внутри Искры — активируется когда куплена
          b010 Беседка Фрактал. CSS opacity управляется через
          `--upgrade-flag-building-fractal-pavilion`. */}
      <div className={styles.fractalConstant} aria-hidden />
      {/* Sensory aura — feeds from `--sensory-aura` CSS variable set by
          UpgradesVisualSync (u010 voice_of_sense). Renders regardless,
          opacity scales from var() so it's invisible until purchased. */}
      <div className={styles.sensoryAura} aria-hidden />
      <motion.div
        className={styles.glow}
        aria-hidden
        animate={{
          scale: breathScale,
          opacity: breathOpacity,
        }}
        transition={{
          duration: isDeeperBreath ? 6 : 4,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      <div className={styles.core} aria-hidden />

      <GuidesLayer />

      <AnimatePresence>
        {ripples.map((r) => (
          <motion.span
            key={r.key}
            className={styles.ripple}
            aria-hidden
            style={{ left: r.x, top: r.y }}
            initial={{ scale: 0, opacity: 0.7 }}
            animate={{ scale: 4, opacity: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            onAnimationComplete={() => {
              removeRipple(r.key);
            }}
          />
        ))}
      </AnimatePresence>
    </button>
  );
}
